import React from "react";
import TwitterEmbed from "./Plugins/TwitterEmbed";

// Utility: safely render HTML
const RenderHTML = ({ html }) => <span dangerouslySetInnerHTML={{ __html: html || "" }} />;

// Recursive function for nested lists
const renderListItems = (items) => {
    if (!items || !Array.isArray(items)) return null;

    return items.map((item, idx) => (
        <li key={idx} className="mb-1">
            <RenderHTML html={item.content || item.text || ""} />
            {item.items && item.items.length > 0 && (
                <ul className="ml-4 list-disc space-y-1">{renderListItems(item.items)}</ul>
            )}
        </li>
    ));
};

// Render table
const renderTable = (tableData) => {
    if (!tableData || !Array.isArray(tableData.content)) return null;

    return (
        <div className="overflow-x-auto my-6">
            <table className="table-auto border bg-white rounded-lg border-gray-300 w-full text-left">
                {tableData?.content?.length > 0 && (
                    <>
                        {/* Table Header */}
                        <thead className="">
                            <tr>
                                {tableData.content[0].map((cell, cellIndex) => (
                                    <th
                                        key={cellIndex}
                                        className="p-2 border-r border-gray-200 font-semibold text-left"
                                    >
                                        <RenderHTML html={cell || ""} />
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        {/* Table Body */}
                        <tbody className="">
                            {tableData.content.slice(1).map((row, rowIndex) => (
                                <tr key={rowIndex} className="border-y border-gray-200">
                                    {row.map((cell, cellIndex) => (
                                        <td key={cellIndex} className="p-2 border-r border-gray-200">
                                            <RenderHTML html={cell || ""} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </>
                )}
            </table>
        </div>

    );
};

const Output = ({ data }) => {
    if (!data || !Array.isArray(data)) return null;

    return (
        <div className="editor space-y-6">
            {data.map((block) => {
                switch (block.type) {
                    case "paragraph":
                        return (
                            <p key={block.id} className="text-base md:text-lg leading-relaxed">
                                <RenderHTML html={block.data.text} />
                            </p>
                        );

                    case "header":
                        const HeaderTag = `h${block.data.level || 3}`;
                        const sizeClass =
                            block.data.level === 2 ? "text-2xl md:text-3xl" :
                                block.data.level === 3 ? "text-xl md:text-2xl" :
                                    "text-lg md:text-xl";
                        return (
                            <HeaderTag key={block.id} className={`font-semibold mt-6 mb-2 ${sizeClass}`}>
                                <RenderHTML html={block.data.text || ""} />
                            </HeaderTag>
                        );

                    case "list":
                        const ListTag = block.data.style === "ordered" ? "ol" : "ul";
                        return (
                            <ListTag key={block.id} className={`ml-6 space-y-2 ${ListTag === "ol" ? "list-decimal" : "list-disc"}`}>
                                {renderListItems(block.data.items)}
                            </ListTag>
                        );

                    case "checklist":
                        return (
                            <ul key={block.id} className="list-inside ml-4 space-y-1">
                                {block.data.items.map((item, idx) => (
                                    <li key={idx} className="flex items-center">
                                        <input type="checkbox" checked={item.checked} readOnly className="mr-2" />
                                        <RenderHTML html={item.text || ""} />
                                    </li>
                                ))}
                            </ul>
                        );

                    case "quote":
                        return (
                            <blockquote key={block.id} className="border-l-4 border-gray-300 pl-4 italic text-gray-700">
                                <RenderHTML html={block.data.text || ""} />
                                {block.data.caption && (
                                    <footer className="mt-1 text-sm text-gray-500">— {block.data.caption}</footer>
                                )}
                            </blockquote>
                        );

                    case "image":
                        return (
                            <div key={block.id} className="my-6">
                                <img
                                    src={block.data.file?.url || ""}
                                    alt={block.data.caption || ""}
                                    className="rounded-lg w-full object-cover"
                                />
                                {block.data.caption && (
                                    <p className="text-sm text-gray-500 mt-2 text-center">{block.data.caption}</p>
                                )}
                            </div>
                        );

                    case "embed":
                        return (
                            <div key={block.id} className="my-6">
                                <iframe
                                    src={block.data.embed || block.data.source || ""}
                                    width="100%"
                                    height="400"
                                    className="rounded-lg"
                                    allowFullScreen
                                    title={`embed-${block.id}`}
                                />
                            </div>
                        );

                    case "code":
                        return (
                            <pre key={block.id} className="bg-gray-100 text-sm p-4 rounded-lg overflow-x-auto">
                                <code>{block.data.code || ""}</code>
                            </pre>
                        );

                    case "inlineCode":
                        return (
                            <code key={block.id} className="bg-gray-200 rounded px-1 py-[1px]">
                                {block.data.code || ""}
                            </code>
                        );

                    case "table":
                        return <div key={block.id}>{renderTable(block.data)}</div>;

                    case "underline":
                        return (
                            <p key={block.id} className="underline">
                                <RenderHTML html={block.data.text || ""} />
                            </p>
                        );

                    case "delimiter":
                        return <hr key={block.id} className="my-6 border-gray-300" />;
                    case "twitter":
                        return (
                            <>
                                <TwitterEmbed url={block?.data?.embed} caption={block?.data?.caption} />
                            </>
                        );
                    default:
                        return null;
                }
            })}
        </div>
    );
};

export default Output;
